import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getEPG, saveEPG, getTemplateStatus, applyTemplate } from "../services/api";
import HelpModal from "../components/HelpModal";
import { Rnd } from "react-rnd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import 'dayjs/locale/es';

dayjs.extend(customParseFormat);
dayjs.locale('es');

const ZOOM_LEVELS = [15, 22, 30, 45, 60];
const SNAP_INTERVAL_MIN = 15;
const TOTAL_SLOTS = 24 * (60 / SNAP_INTERVAL_MIN);
const MAX_EDITABLE_DAYS_FUTURE = 30;
const MAX_DESC_LENGTH = 250;
const WEEK_COLORS = ['bg-gray-700','bg-gray-500','bg-slate-600','bg-zinc-600','bg-stone-600','bg-neutral-600','bg-gray-600','bg-slate-700'];

const computePositions = (programList, sh) => {
  const newPosMap = {};
  programList.forEach((p) => {
    const start = safeParse(p.start);
    const duration = p.duration ? parseDurationToMinutes(p.duration) : 0;
    if (start && start.isValid() && p.__internalId) {
      newPosMap[p.__internalId] = {
        y: Math.round((start.hour() * 60 + start.minute()) / SNAP_INTERVAL_MIN) * sh,
        height: Math.round(duration / SNAP_INTERVAL_MIN) * sh
      };
    }
  });
  return newPosMap;
};

const parseDurationToMinutes = (durationStr) => {
  if (typeof durationStr !== 'string' || !durationStr.includes(':')) return 0;
  const parts = durationStr.split(":").map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return 0;
  return parts[0] * 60 + parts[1];
};

const formatMinutesToDuration = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${m.toString().padStart(2, "0")}:00`;
};

const safeParse = (dateStr) => {
  const cleaned = dateStr?.trim().replace(/\r/g, "").replace(/\n/g, "");
  const parsed = dayjs(cleaned, "YYYY/MM/DD HH:mm:ss", true);
  return parsed.isValid() ? parsed : null;
};

const getProgramKey = (p) => p.__internalId;
const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

const getMonday = (dateStr) => {
  const d = dayjs(dateStr);
  const dow = d.day();
  const offset = dow === 0 ? -6 : 1 - dow;
  return d.add(offset, 'day').format('YYYY-MM-DD');
};

const getWeekDays = (dateStr) => {
  const d = dayjs(dateStr);
  const dow = d.day();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return Array.from({ length: 7 }, (_, i) => d.add(mondayOffset + i, 'day'));
};

const serializeForComparison = (progs) =>
  JSON.stringify(
    [...progs].sort((a, b) => a.start.localeCompare(b.start))
              .map(({ __internalId, ...rest }) => rest)
  );

export default function GridEditor() {
  const [programmes, setProgrammes] = useState([]);
  const [positions, setPositions] = useState({});
  const [currentDate, setCurrentDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [editingProgram, setEditingProgram] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pendingAddY, setPendingAddY] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(null); // { date, y } for week view
  const [hasChanges, setHasChanges] = useState(false);
  const [savedJson, setSavedJson] = useState('');
  const [notification, setNotification] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ message: '', onConfirm: () => {}, onCancel: () => {} });
  const [showHelp, setShowHelp] = useState(false);
  const [channelDisplayName, setChannelDisplayName] = useState("Canal Único");
  const [zoomIdx, setZoomIdx] = useState(2);
  const [viewMode, setViewMode] = useState('day');

  const slotHeight = ZOOM_LEVELS[zoomIdx];
  const programmesRef = useRef(programmes);
  programmesRef.current = programmes;

  const navigate = useNavigate();

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => { setNotification(null); }, 3000);
  }, []);

  const showOverlapNotification = useCallback(() => {
    showNotification("❌ ¡Este bloque se solapa con otro!", "error");
  }, [showNotification]);

  const updateProgrammesState = useCallback((newProgrammesList) => {
    setProgrammes(newProgrammesList);
  }, []);

  const calculateAndSetPositions = useCallback((programList) => {
    setPositions(computePositions(programList, ZOOM_LEVELS[zoomIdx]));
  }, [zoomIdx]);

  useEffect(() => {
    const sh = ZOOM_LEVELS[zoomIdx];
    setPositions(computePositions(programmesRef.current, sh));
  }, [zoomIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!savedJson) return;
    setHasChanges(serializeForComparison(programmes) !== savedJson);
  }, [programmes, savedJson]);

  const loadEPGFromAPI = useCallback(async (file) => {
    try {
      const res = await getEPG(file);
      const list = (res.data.programmes || []).map((p) => ({
        ...p,
        desc: p.desc || "",
        channel: p.channel || "UnknownChannel",
        __internalId: p.__internalId || generateId()
      }));
      setProgrammes(list);
      setSavedJson(serializeForComparison(list));
      setPositions(computePositions(list, ZOOM_LEVELS[zoomIdx]));
      return list;
    } catch (error) {
      if (!error.response || error.response.status !== 401) {
        showNotification("🚫 Error al cargar la guía EPG.", "error");
      }
      throw error;
    }
  }, [showNotification, zoomIdx]);

  useEffect(() => {
    const userDataString = localStorage.getItem("user");
    let user = null;
    if (userDataString) {
      try { user = JSON.parse(userDataString); }
      catch (e) { localStorage.removeItem('user'); }
    }
    if (user && user.token && user.file) {
      let title = user.channel_title || user.file.split('.')[0];
      setChannelDisplayName(title);
      document.title = `Editor EPG – ${title}`;
      getTemplateStatus(user.file)
        .then(res => {
          if (!res.data.complete) {
            showNotification("⚠️ Configurá la plantilla semanal antes de editar.", "warning");
            navigate('/plantilla');
            return;
          }
          loadEPGFromAPI(user.file)
            .then(list => { if (list) checkAndAutoFill(user.file, list); })
            .catch(() => {});
        })
        .catch(() => {
          showNotification("⚠️ No se pudo verificar la plantilla.", "warning");
          navigate('/plantilla');
        });
    } else {
      navigate('/');
    }
  }, [loadEPGFromAPI, navigate, setChannelDisplayName]);

  const getChannelForNewProgram = () => {
    if (programmes.length > 0 && programmes[0].channel) return programmes[0].channel;
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.channel_title) return user.channel_title;
    if (user?.file) return user.file.split('.')[0];
    return "UnknownChannel";
  };

  const handleAdd = () => {
    if (pendingAddY === null) return;
    const minutes = Math.round(pendingAddY / slotHeight) * SNAP_INTERVAL_MIN;
    const duration = SNAP_INTERVAL_MIN;
    const newStart = dayjs(currentDate).startOf("day").add(minutes, "minute");
    const newStartMin = minutes;
    const newEndMin = minutes + duration;
    const overlaps = programmes.some((p) => {
      const start = safeParse(p.start);
      if (!start || start.format("YYYY-MM-DD") !== currentDate) return false;
      const existingStartMin = start.hour() * 60 + start.minute();
      const existingEndMin = existingStartMin + parseDurationToMinutes(p.duration);
      return newStartMin < existingEndMin && newEndMin > existingStartMin;
    });
    if (overlaps) { showOverlapNotification(); setPendingAddY(null); return; }

    const newProgram = {
      title: "Nuevo programa", desc: "",
      start: newStart.format("YYYY/MM/DD HH:mm:ss"),
      duration: formatMinutesToDuration(duration),
      channel: getChannelForNewProgram(),
      __internalId: generateId()
    };
    const updatedProgrammes = [...programmes, newProgram];
    updateProgrammesState(updatedProgrammes);
    calculateAndSetPositions(updatedProgrammes);
    setPendingAddY(null);
    showNotification("✨ Nuevo programa añadido.", "info");
  };

  const handleAddForDate = useCallback((dateStr, y) => {
    const minutes = Math.round(y / slotHeight) * SNAP_INTERVAL_MIN;
    const duration = SNAP_INTERVAL_MIN;
    const newStart = dayjs(dateStr).startOf("day").add(minutes, "minute");
    const newStartMin = minutes;
    const newEndMin = minutes + duration;
    const overlaps = programmes.some((p) => {
      const start = safeParse(p.start);
      if (!start || start.format("YYYY-MM-DD") !== dateStr) return false;
      const existingStartMin = start.hour() * 60 + start.minute();
      const existingEndMin = existingStartMin + parseDurationToMinutes(p.duration);
      return newStartMin < existingEndMin && newEndMin > existingStartMin;
    });
    if (overlaps) { showOverlapNotification(); setPendingAdd(null); return; }

    const newProgram = {
      title: "Nuevo programa", desc: "",
      start: newStart.format("YYYY/MM/DD HH:mm:ss"),
      duration: formatMinutesToDuration(duration),
      channel: getChannelForNewProgram(),
      __internalId: generateId()
    };
    const updatedProgrammes = [...programmes, newProgram];
    updateProgrammesState(updatedProgrammes);
    calculateAndSetPositions(updatedProgrammes);
    setPendingAdd(null);
    showNotification("✨ Nuevo programa añadido.", "info");
  }, [programmes, slotHeight, showOverlapNotification, updateProgrammesState, calculateAndSetPositions, showNotification]);

  const updateProgram = useCallback((p, newY, newHeight) => {
    const id = getProgramKey(p);
    const sh = ZOOM_LEVELS[zoomIdx];
    const progDate = safeParse(p.start)?.format("YYYY-MM-DD") || currentDate;
    const minutes = Math.round(newY / sh) * SNAP_INTERVAL_MIN;
    const duration = Math.max(SNAP_INTERVAL_MIN, Math.round(newHeight / sh) * SNAP_INTERVAL_MIN);
    const startTime = dayjs(progDate).startOf("day").add(minutes, "minute");
    const newStart = startTime.format("YYYY/MM/DD HH:mm:ss");
    const newDuration = formatMinutesToDuration(duration);
    const proposedEndMin = minutes + duration;

    const currentProgramData = programmes.find(prog => getProgramKey(prog) === id);
    if (!currentProgramData) return;
    const currentStartParsed = safeParse(currentProgramData.start);
    const currentDurationMinutes = parseDurationToMinutes(currentProgramData.duration);
    const currentYMinutes = currentStartParsed ? (currentStartParsed.hour() * 60 + currentStartParsed.minute()) : 0;
    const snappedCurrentY = Math.round(currentYMinutes / SNAP_INTERVAL_MIN) * sh;
    const snappedCurrentHeight = Math.round(currentDurationMinutes / SNAP_INTERVAL_MIN) * sh;

    if (snappedCurrentY === newY && snappedCurrentHeight === Math.round(duration / SNAP_INTERVAL_MIN) * sh) return;

    const overlaps = programmes.some((other) => {
      if (getProgramKey(other) === id) return false;
      const otherStart = safeParse(other.start);
      if (!otherStart || otherStart.format("YYYY-MM-DD") !== progDate) return false;
      const existingStartMin = otherStart.hour() * 60 + otherStart.minute();
      const existingEndMin = existingStartMin + parseDurationToMinutes(other.duration);
      return minutes < existingEndMin && proposedEndMin > existingStartMin;
    });

    if (overlaps) { showOverlapNotification(); return; }

    const updatedProgrammes = programmes.map(prog =>
      getProgramKey(prog) === id ? { ...prog, start: newStart, duration: newDuration } : prog
    );
    updateProgrammesState(updatedProgrammes);
    calculateAndSetPositions(updatedProgrammes);
  }, [currentDate, programmes, showOverlapNotification, updateProgrammesState, calculateAndSetPositions, zoomIdx]);

  const deleteProgram = useCallback((id) => {
    setConfirmConfig({
      message: "¿Estás seguro de que deseas eliminar este programa?",
      onConfirm: () => {
        const updatedProgrammes = programmes.filter(p => p.__internalId !== id);
        updateProgrammesState(updatedProgrammes);
        calculateAndSetPositions(updatedProgrammes);
        showNotification("🗑️ Programa eliminado.", "success");
        setShowConfirmModal(false);
      },
      onCancel: () => { showNotification("Operación cancelada.", "info"); setShowConfirmModal(false); }
    });
    setShowConfirmModal(true);
  }, [updateProgrammesState, showNotification, programmes, calculateAndSetPositions]);

  const openEditModal = (p) => { setEditingProgram(p); setEditTitle(p.title); setEditDesc(p.desc || ""); setShowModal(true); };
  const saveEdit = () => {
    if (!editingProgram) return;
    const id = editingProgram.__internalId;
    const updatedProgrammes = programmes.map(prog =>
      getProgramKey(prog) === id ? { ...prog, title: editTitle, desc: editDesc } : prog
    );
    updateProgrammesState(updatedProgrammes);
    setShowModal(false);
    showNotification("✏️ Cambios guardados.", "success");
  };
  const cancelEdit = () => { setShowModal(false); setEditingProgram(null); };

  const checkAndAutoFill = useCallback(async (file, loadedProgrammes) => {
    const mondays = [0, 1, 2, 3].map(n =>
      getMonday(dayjs().add(n * 7, 'day').format('YYYY-MM-DD'))
    );
    const emptyWeeks = mondays.filter(monday => {
      const mondayDjs = dayjs(monday);
      return !loadedProgrammes.some(p => {
        const d = safeParse(p.start);
        return d && !d.isBefore(mondayDjs, 'day') && !d.isAfter(mondayDjs.add(6, 'day'), 'day');
      });
    });
    for (const monday of emptyWeeks) {
      try { await applyTemplate(file, monday, 'overwrite'); } catch (_) {}
    }
    if (emptyWeeks.length > 0) await loadEPGFromAPI(file).catch(() => {});
  }, [loadEPGFromAPI]);

  const handleSave = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.file) { showNotification("🚫 Usuario no autenticado.", "error"); return; }
    try {
      await saveEPG(user.file, programmes);
      showNotification("💾 ¡Cambios guardados!", "success");
      setSavedJson(serializeForComparison(programmes));
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "🚫 Error al guardar EPG";
      if (errorDetail) {
        if (Array.isArray(errorDetail)) errorMessage += ": " + errorDetail.map(err => `${err.loc?.join('.') || 'field'} - ${err.msg}`).join('; ');
        else if (typeof errorDetail === 'string') errorMessage += `: ${errorDetail}`;
      }
      showNotification(errorMessage, "error");
    }
  }, [programmes, showNotification]);

  const handleCancelChanges = useCallback(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.file) {
      loadEPGFromAPI(user.file)
        .then(() => showNotification("❌ Cambios descartados.", "info"))
        .catch(() => showNotification("🚫 Error al recargar.", "error"));
    }
  }, [loadEPGFromAPI, showNotification]);


  const handleLogout = useCallback(() => {
    localStorage.removeItem('user');
    showNotification("Has cerrado sesión.", "info");
    navigate('/');
  }, [navigate, showNotification]);

  const weekDays = getWeekDays(currentDate);
  const today = dayjs().startOf('day');

  const renderProgram = (p, idx, compact = false) => {
    const key = getProgramKey(p);
    const pos = positions[key];
    const start = safeParse(p.start);
    if (!pos || !start?.isValid()) return null;
    const duration = parseDurationToMinutes(p.duration);
    const progDate = start.format('YYYY-MM-DD');
    const isPast = dayjs(progDate).isBefore(dayjs(), 'day');
    const color = idx % 2 === 0 ? "bg-orange-950 hover:bg-orange-800" : "bg-stone-700 hover:bg-orange-800";

    if (compact) {
      return (
        <Rnd key={key} size={{ width: "100%", height: pos.height }} position={{ x: 0, y: pos.y }}
          bounds="parent" dragAxis="y"
          dragGrid={[1, slotHeight]} resizeGrid={[0, slotHeight]}
          disableDragging={isPast}
          enableResizing={isPast ? { top: false, right: false, bottom: false, left: false, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false } : { top: false, right: false, bottom: true, left: false, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}
          onDragStop={(e, d) => { const snapY = Math.round(d.y / slotHeight) * slotHeight; updateProgram(p, snapY, pos.height); }}
          onResizeStop={(e, dir, ref, delta, posSnap) => { const newH = Math.max(slotHeight, Math.round(ref.offsetHeight / slotHeight) * slotHeight); const newY = Math.round(posSnap.y / slotHeight) * slotHeight; updateProgram(p, newY, newH); }}
          className={`absolute rounded shadow overflow-hidden text-white transition-colors duration-150 group ${color} ${isPast ? 'opacity-60' : ''}`}
          style={{ border: '1px solid #6366f1', zIndex: 30 }}>
          <div className="relative h-full px-1 py-0.5">
            <div className="font-semibold text-xs truncate pr-8 leading-tight">{p.title}</div>
            {pos.height >= slotHeight * 2 && (
              <div className="text-xs opacity-75 leading-tight">{start.format("HH:mm")}→{start.add(duration, "minute").format("HH:mm")}</div>
            )}
            {pos.height >= slotHeight * 3 && p.desc && (
              <div className="text-[10px] opacity-60 leading-tight truncate mt-0.5">{p.desc}</div>
            )}
            {!isPast && <div className="absolute top-0 right-0 flex opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
              <button onClick={(e) => { e.stopPropagation(); if (pos.height > slotHeight) updateProgram(p, pos.y, pos.height - slotHeight); }} disabled={pos.height <= slotHeight} className="text-white hover:text-orange-300 bg-black/40 hover:bg-black/60 p-0.5 disabled:opacity-30" title="-15 min">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                  <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
              <button onClick={(e) => { e.stopPropagation(); updateProgram(p, pos.y, pos.height + slotHeight); }} className="text-white hover:text-green-300 bg-black/40 hover:bg-black/60 p-0.5" title="+15 min">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
              <button onClick={(e) => { e.stopPropagation(); openEditModal(p); }} className="text-white bg-black/40 hover:bg-black/60 p-0.5" title="Editar">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              </button>
              <button onClick={(e) => { e.stopPropagation(); deleteProgram(key); }} className="text-white bg-black/40 hover:bg-red-600 p-0.5" title="Eliminar">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>}
          </div>
        </Rnd>
      );
    }

    return (
      <Rnd key={key} size={{ width: "100%", height: pos.height }} position={{ x: 0, y: pos.y }}
        bounds="parent" dragAxis="y"
        dragGrid={[1, slotHeight]} resizeGrid={[0, slotHeight]}
        enableResizing={{ top: false, right: false, bottom: true, left: false, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}
        onDragStop={(e, d) => { const snapY = Math.round(d.y / slotHeight) * slotHeight; updateProgram(p, snapY, pos.height); }}
        onResizeStop={(e, dir, ref, delta, posSnap) => { const newH = Math.max(slotHeight, Math.round(ref.offsetHeight / slotHeight) * slotHeight); const newY = Math.round(posSnap.y / slotHeight) * slotHeight; updateProgram(p, newY, newH); }}
        className={`absolute rounded px-3 py-2 shadow overflow-hidden text-white transition-colors duration-150 ${color}`}
        style={{ border: '1px solid #6366f1', zIndex: 30 }}>
        <div className="flex justify-between items-start">
          <div className="font-bold text-sm truncate">{p.title}</div>
          <div className="flex gap-3">
            <button onClick={(e) => { e.stopPropagation(); if (pos.height > slotHeight) updateProgram(p, pos.y, pos.height - slotHeight); }} disabled={pos.height <= slotHeight} className="text-white hover:text-orange-300 p-0.5 rounded-full hover:bg-black/20 disabled:opacity-30" title="-15 min">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button onClick={(e) => { e.stopPropagation(); updateProgram(p, pos.y, pos.height + slotHeight); }} className="text-white hover:text-green-300 p-0.5 rounded-full hover:bg-black/20" title="+15 min">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <button onClick={(e) => { e.stopPropagation(); openEditModal(p); }} className="text-white hover:text-yellow-200 p-0.5 rounded-full hover:bg-black/20" title="Editar">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
            </button>
            <button onClick={(e) => { e.stopPropagation(); deleteProgram(key); }} className="text-white hover:text-red-300 p-0.5 rounded-full hover:bg-black/20" title="Eliminar">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
          </div>
        </div>
        <div className="text-xs opacity-90">{start.format("HH:mm")} → {start.add(duration, "minute").format("HH:mm")}</div>
        {pos.height >= slotHeight * 2 && p.desc && (
          <div className="text-xs opacity-60 mt-1 line-clamp-2 leading-snug">{p.desc}</div>
        )}
      </Rnd>
    );
  };

  return (
    <>
      {notification && (
        <div className={`fixed top-4 right-4 z-[1000] px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 text-sm font-semibold
          ${notification.type === 'success' ? 'bg-green-600 text-white' : ''}
          ${notification.type === 'error'   ? 'bg-red-600 text-white' : ''}
          ${notification.type === 'warning' ? 'bg-yellow-500 text-gray-900' : ''}
          ${notification.type === 'info'    ? 'bg-blue-500 text-white' : ''}`}>
          {notification.type === 'success' && <span>✅</span>}
          {notification.type === 'error'   && <span>🛑</span>}
          {notification.type === 'warning' && <span>⚠️</span>}
          {notification.type === 'info'    && <span>ℹ️</span>}
          <span>{notification.message}</span>
        </div>
      )}

      {/* ── SIDEBAR ── */}
      <div className="fixed left-0 top-0 bottom-0 w-52 bg-gray-900 text-white z-50 flex flex-col overflow-y-auto shadow-2xl">

        {/* Cabecera */}
        <div className="px-4 py-3 border-b border-gray-700 bg-gray-900">
          <div className="flex items-center gap-2">
            <span className="text-lg">📺</span>
            <div>
              <div className="font-bold text-sm leading-tight">Editor EPG</div>
              <div className="text-[11px] text-gray-400">{channelDisplayName}</div>
            </div>
          </div>
        </div>

        {/* ── SECCIÓN ACTIVA: PROGRAMACIÓN ── */}
        <div className="border-l-4 border-orange-500 bg-gray-800">
          <div className="px-3 py-2.5 bg-gray-700 flex items-center gap-2 border-b border-gray-600">
            <span className="text-base">📡</span>
            <span className="text-sm font-bold text-orange-300">Programación</span>
            <span className="ml-auto text-[9px] bg-orange-600 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">activo</span>
          </div>
          <div className="p-3 space-y-3">

            {/* Guardar / Restaurar */}
            <div className="flex gap-1">
              <button onClick={handleSave} disabled={!hasChanges}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs py-1.5 rounded font-medium">
                💾 Guardar
              </button>
              <button onClick={handleCancelChanges} disabled={!hasChanges} title="Descartar cambios"
                className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs px-2.5 py-1.5 rounded">
                ↺
              </button>
            </div>

            {/* Navegación de fecha */}
            {viewMode === 'day' ? (
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentDate(dayjs(currentDate).subtract(1, 'day').format('YYYY-MM-DD'))}
                  disabled={dayjs(currentDate).diff(dayjs(), 'days') <= -2}
                  className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-7 h-7 rounded text-sm flex items-center justify-center">←</button>
                <div className="flex-1 text-center">
                  <div className="text-xs font-semibold capitalize leading-tight">{dayjs(currentDate).format('dddd')}</div>
                  <div className="text-[10px] text-gray-400">{dayjs(currentDate).format('D MMM YYYY')}</div>
                </div>
                <button onClick={() => setCurrentDate(dayjs(currentDate).add(1, 'day').format('YYYY-MM-DD'))}
                  disabled={dayjs(currentDate).diff(dayjs(), 'days') >= MAX_EDITABLE_DAYS_FUTURE}
                  className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-7 h-7 rounded text-sm flex items-center justify-center">→</button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentDate(dayjs(currentDate).subtract(7, 'day').format('YYYY-MM-DD'))}
                  disabled={dayjs(weekDays[0]).diff(dayjs(), 'days') <= -2}
                  className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-7 h-7 rounded text-sm flex items-center justify-center">←</button>
                <div className="flex-1 text-center">
                  <div className="text-xs font-semibold text-gray-300 capitalize">{dayjs(currentDate).format('MMMM YYYY')}</div>
                  <div className="text-[10px] text-gray-500">{weekDays[0].format('D')} – {weekDays[6].format('D MMM')}</div>
                </div>
                <button onClick={() => setCurrentDate(dayjs(currentDate).add(7, 'day').format('YYYY-MM-DD'))}
                  disabled={dayjs(weekDays[6]).diff(dayjs(), 'days') >= MAX_EDITABLE_DAYS_FUTURE}
                  className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-7 h-7 rounded text-sm flex items-center justify-center">→</button>
              </div>
            )}

            {/* Mini calendario semanal */}
            <div>
              <div className="text-[10px] text-gray-500 text-center capitalize mb-1">{dayjs(currentDate).format('MMMM YYYY')}</div>
              <div className="grid grid-cols-7 gap-0.5">
                {weekDays.map((day) => {
                  const dateStr = day.format('YYYY-MM-DD');
                  const isSelected = dateStr === currentDate;
                  const isToday = dateStr === dayjs().format('YYYY-MM-DD');
                  const isDisabled = day.isBefore(today.subtract(2, 'day'), 'day') || day.isAfter(today.add(MAX_EDITABLE_DAYS_FUTURE, 'day'), 'day');
                  return (
                    <button key={dateStr} onClick={() => { if (!isDisabled) { setCurrentDate(dateStr); setViewMode('day'); } }} disabled={isDisabled}
                      className={`flex flex-col items-center py-0.5 rounded text-[9px] transition
                        ${isSelected ? 'bg-orange-500 text-white font-bold' : ''}
                        ${!isSelected && !isDisabled ? 'hover:bg-gray-700 text-gray-300' : ''}
                        ${isDisabled ? 'opacity-20 cursor-not-allowed' : ''}
                        ${isToday && !isSelected ? 'text-orange-300' : ''}`}>
                      <span className="uppercase leading-tight">{day.format('dd')[0]}</span>
                      <span className="leading-tight">{day.format('D')}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Vista y zoom */}
            <div className="space-y-1.5 pt-1 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500 uppercase tracking-wide">Zoom</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setZoomIdx(i => Math.max(0, i - 1))} disabled={zoomIdx === 0}
                    className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-5 h-5 rounded text-xs flex items-center justify-center">−</button>
                  <span className="text-[10px] text-gray-400 w-8 text-center">{Math.round((slotHeight / 30) * 100)}%</span>
                  <button onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))} disabled={zoomIdx === ZOOM_LEVELS.length - 1}
                    className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-5 h-5 rounded text-xs flex items-center justify-center">+</button>
                </div>
              </div>
              <button onClick={() => {
                if (viewMode === 'day') { setCurrentDate(dayjs().format('YYYY-MM-DD')); setViewMode('week'); }
                else { setViewMode('day'); }
              }} className={`w-full text-xs py-1.5 rounded transition ${viewMode === 'week' ? 'bg-orange-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'}`}>
                {viewMode === 'week' ? '📺 Vista por día' : '📅 Vista semanal'}
              </button>
            </div>
          </div>
        </div>

        {/* ── SEPARADOR DE NAVEGACIÓN ── */}
        <div className="flex items-center gap-2 px-3 pt-3 pb-1">
          <div className="flex-1 border-t border-gray-700" />
          <span className="text-[9px] text-gray-600 uppercase tracking-widest whitespace-nowrap">Ir a</span>
          <div className="flex-1 border-t border-gray-700" />
        </div>

        {/* ── NAV: PLANTILLA ── */}
        <button onClick={() => navigate('/plantilla')}
          className="w-full flex items-center gap-3 px-4 py-3 border-l-4 border-emerald-900 bg-emerald-950 hover:border-emerald-500 hover:bg-gray-800 text-emerald-300 hover:text-white transition">
          <span className="text-base">📋</span>
          <span className="text-sm font-medium">Plantilla</span>
          <span className="ml-auto text-emerald-700 text-sm">›</span>
        </button>

        {/* ── NAV: AYUDA ── */}
        <button onClick={() => setShowHelp(true)}
          className="w-full flex items-center gap-3 px-4 py-3 border-l-4 border-transparent hover:border-gray-500 hover:bg-gray-800 text-gray-400 hover:text-white transition">
          <span className="text-base">❓</span>
          <span className="text-sm font-medium">Ayuda</span>
          <span className="ml-auto text-gray-600 text-sm">›</span>
        </button>

        {/* Salir */}
        <div className="mt-auto p-3">
          <button onClick={handleLogout} className="w-full bg-red-800 hover:bg-red-700 text-white text-xs py-2 rounded transition">
            Salir
          </button>
        </div>
      </div>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="ml-52 bg-gray-100 min-h-screen font-sans">

        {/* WEEK VIEW */}
        {viewMode === 'week' && (
          <div className="p-4">
            <div className="rounded shadow border border-gray-200">
              {/* Day headers */}
              <div className="flex border-b-2 border-orange-700 bg-orange-700 sticky top-0 z-40 rounded-t overflow-hidden">
                <div className="w-16 shrink-0 border-r border-gray-200" />
                {weekDays.map((day) => {
                  const dateStr = day.format('YYYY-MM-DD');
                  const isToday = dateStr === dayjs().format('YYYY-MM-DD');
                  const isSelected = dateStr === currentDate;
                  const isDisabled = day.isBefore(today.subtract(2, 'day'), 'day') || day.isAfter(today.add(MAX_EDITABLE_DAYS_FUTURE, 'day'), 'day');
                  return (
                    <div key={dateStr} onClick={() => { setCurrentDate(dateStr); setViewMode('day'); }}
                      className={`flex-1 text-center py-2 text-sm font-semibold border-r border-gray-200 cursor-pointer transition select-none
                        ${isSelected ? 'bg-orange-500 text-white' : 'hover:bg-orange-800 text-orange-100'}
                        ${isToday ? 'underline' : ''}
                        ${isDisabled ? 'opacity-40' : ''}`}>
                      <div className="uppercase text-xs">{day.format('ddd')}</div>
                      <div>{day.format('D MMM')}</div>
                    </div>
                  );
                })}
              </div>
              {/* Grid */}
              <div className="flex overflow-x-auto overflow-y-hidden rounded-b">
                {/* Time column */}
                <div className="w-16 shrink-0 border-r border-gray-200 text-xs text-gray-400 select-none" style={{ height: slotHeight * TOTAL_SLOTS }}>
                  {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} style={{ height: slotHeight * 4 }} className="border-b border-gray-100 flex items-start px-1 pt-0.5">
                      {h.toString().padStart(2, '0')}:00
                    </div>
                  ))}
                </div>
                {/* Day columns */}
                {weekDays.map((day) => {
                  const dateStr = day.format('YYYY-MM-DD');
                  const isPastDay = day.isBefore(dayjs(), 'day');
                  const isFutureLimit = day.isAfter(today.add(MAX_EDITABLE_DAYS_FUTURE, 'day'), 'day');
                  const isDisabled = isPastDay || isFutureLimit;
                  const dayProgs = programmes
                    .filter(p => { const s = safeParse(p.start); return s && s.format('YYYY-MM-DD') === dateStr; })
                    .sort((a, b) => safeParse(a.start).diff(safeParse(b.start)));
                  return (
                    <div key={dateStr} className={`flex-1 relative border-r border-gray-100
                      ${isPastDay ? 'opacity-50 grayscale pointer-events-none' : ''}
                      ${isFutureLimit ? 'opacity-30 pointer-events-none' : ''}`}
                      style={{ height: slotHeight * TOTAL_SLOTS, minWidth: 80 }}
                      onClick={(e) => {
                        if (isDisabled || e.target !== e.currentTarget) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const snapY = Math.round(y / slotHeight) * slotHeight;
                        setPendingAdd({ date: dateStr, y: snapY });
                      }}>
                      {/* Hour lines */}
                      {Array.from({ length: 24 }).map((_, h) => (
                        <div key={h} className="absolute left-0 right-0 border-b border-gray-100" style={{ top: h * slotHeight * 4, height: slotHeight * 4 }} />
                      ))}
                      {/* Programs */}
                      {dayProgs.map((p, idx) => renderProgram(p, idx, true))}
                      {/* Pending add indicator */}
                      {pendingAdd?.date === dateStr && (
                        <div className="absolute left-0 right-0 px-1 z-40" style={{ top: pendingAdd.y, height: slotHeight }}>
                          <div className="bg-green-100 text-green-800 px-1 py-0.5 rounded shadow flex justify-between items-center h-full text-xs border border-green-300 animate-pulse">
                            <span className="truncate">Agregar</span>
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); handleAddForDate(pendingAdd.date, pendingAdd.y); }} className="bg-green-600 text-white px-1 rounded hover:bg-green-700">✓</button>
                              <button onClick={(e) => { e.stopPropagation(); setPendingAdd(null); }} className="text-red-500 hover:text-red-700">✕</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* DAY VIEW */}
        {viewMode === 'day' && (
          <div className="p-4">
            <h2 className="sticky top-0 z-40 text-center text-white font-semibold capitalize py-2 text-sm bg-orange-700 border-b border-orange-800 shadow-sm mb-3">
              {dayjs(currentDate).format('dddd D [de] MMMM YYYY')}
            </h2>
            <div className="flex max-w-3xl mx-auto bg-white rounded shadow overflow-hidden border border-gray-200">
              <div className="w-20 border-r border-gray-200 text-xs text-gray-500 select-none">
                {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
                  const minutes = i * SNAP_INTERVAL_MIN;
                  const hour = Math.floor(minutes / 60);
                  const min = minutes % 60;
                  const label = `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
                  const slotStartMin = minutes;
                  const slotEndMin = minutes + SNAP_INTERVAL_MIN;
                  const isOccupied = programmes.some(p => {
                    const start = safeParse(p.start);
                    if (!start || start.format("YYYY-MM-DD") !== currentDate) return false;
                    const startMin = start.hour() * 60 + start.minute();
                    const endMin = startMin + parseDurationToMinutes(p.duration);
                    return slotStartMin < endMin && slotEndMin > startMin;
                  });
                  return (
                    <div key={`timeslot-${i}`} style={{ height: slotHeight }} className="px-2 py-1 border-b border-gray-100 relative group flex items-center justify-between">
                      {label}
                      {!isOccupied && <button onClick={() => setPendingAddY(i * slotHeight)} className="text-green-600 text-sm ml-auto opacity-0 group-hover:opacity-100 transition-opacity">＋</button>}
                    </div>
                  );
                })}
              </div>
              <div className="flex-1 relative" style={{ height: slotHeight * TOTAL_SLOTS }}>
                {programmes
                  .filter(p => { const start = safeParse(p.start); return positions[getProgramKey(p)] && start && start.format("YYYY-MM-DD") === currentDate; })
                  .sort((a, b) => { const sA = safeParse(a.start); const sB = safeParse(b.start); if (!sA || !sB) return 0; return sA.diff(sB); })
                  .map((p, idx) => renderProgram(p, idx, false))}
                {pendingAddY !== null && (
                  <div className="absolute left-0 right-0 px-2" style={{ top: pendingAddY, height: slotHeight }}>
                    <div className="bg-green-100 text-green-800 px-2 py-1 rounded shadow flex justify-between items-center h-full text-sm border border-green-300 animate-pulse">
                      <span>Agregar nuevo programa</span>
                      <div className="flex gap-2">
                        <button onClick={handleAdd} className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 text-xs">Agregar</button>
                        <button onClick={() => setPendingAddY(null)} className="text-red-500 text-xs hover:text-red-700">Cancelar</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Editar programa</h2>
            <div className="mb-4">
              <label className="block font-semibold text-sm mb-1 text-gray-700">Título</label>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-1">
                <label className="font-semibold text-sm text-gray-700">Descripción</label>
                <span className={`text-xs ${editDesc.length >= MAX_DESC_LENGTH ? 'text-red-600 font-semibold' : editDesc.length >= MAX_DESC_LENGTH - 25 ? 'text-amber-600' : 'text-gray-400'}`}>
                  {editDesc.length}/{MAX_DESC_LENGTH}
                </span>
              </div>
              <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} maxLength={MAX_DESC_LENGTH} className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm resize-y min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={cancelEdit} className="bg-gray-300 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-400">Cancelar</button>
              <button onClick={saveEdit} className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700">Guardar</button>
            </div>
          </div>
        </div>
      )}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Confirmación</h3>
            <p className="text-gray-700 mb-6 text-sm whitespace-pre-wrap">{confirmConfig.message}</p>
            <div className="flex justify-end gap-3">
              <button onClick={confirmConfig.onCancel} className="bg-gray-300 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-400">Cancelar</button>
              <button onClick={confirmConfig.onConfirm} className="bg-red-500 text-white px-5 py-2 rounded-md hover:bg-red-600">Confirmar</button>
            </div>
          </div>
        </div>
      )}
      <HelpModal open={showHelp} onClose={() => setShowHelp(false)} accent="orange" />
    </>
  );
}
